import { PUBLIC_SITE_PAGES, PUNTOPY_SITE_ALIASES, PUNTOPY_SITE_IMAGE, PUNTOPY_SITE_NAME, PUNTOPY_SITE_ORIGIN, type PublicBusiness, type PublicPageMetadata } from "@puntopy/contracts";

export interface PageSeo extends Omit<PublicPageMetadata, "path"> {
  path: string | null;
  indexable: boolean;
  structuredData?: Record<string, unknown>;
}

export const privatePageSeo: PageSeo = { path: null, title: "Acceso a tu cuenta | PuntoPY", description: "Acceso privado a las cuentas y paneles de PuntoPY.", indexable: false };
export const catalogShellSeo: PageSeo = { path: null, title: "Catálogo de comercio | PuntoPY", description: "Consultá el catálogo publicado por este comercio en PuntoPY.", indexable: true };
export const notFoundSeo: PageSeo = { path: null, title: "Página no encontrada | PuntoPY", description: "Esta página no está disponible. Volvé al buscador público de PuntoPY.", indexable: false };

export function publicPageSeo(path: string): PageSeo {
  const page = PUBLIC_SITE_PAGES.find((item) => item.path === path);
  if (!page) throw new Error("La página no forma parte del sitio público estático.");
  return {
    ...page, indexable: true,
    structuredData: path === "/" ? {
      "@context": "https://schema.org", "@type": "WebSite", "@id": `${PUNTOPY_SITE_ORIGIN}/#website`,
      name: PUNTOPY_SITE_NAME, alternateName: PUNTOPY_SITE_ALIASES,
      url: `${PUNTOPY_SITE_ORIGIN}/`, description: page.description, inLanguage: "es-PY"
    } : {
      "@context": "https://schema.org", "@type": "WebPage", name: page.title,
      url: PUNTOPY_SITE_ORIGIN + path, description: page.description, inLanguage: "es-PY",
      isPartOf: { "@id": `${PUNTOPY_SITE_ORIGIN}/#website` }
    }
  };
}

export function businessPageSeo(business: PublicBusiness): PageSeo {
  const path = `/comercio/${encodeURIComponent(business.slug)}`;
  const place = business.city ? ` en ${business.city}` : "";
  const title = `${business.name}${place} | PuntoPY`;
  const description = `${business.name}${place}. ${business.description || "Consultá su catálogo y disponibilidad, y contactá directamente al comercio desde PuntoPY."}`.replace(/\s+/g, " ").trim().slice(0, 180);
  return {
    path, title, description, indexable: true,
    structuredData: {
      "@context": "https://schema.org", "@type": "CollectionPage", name: title,
      url: PUNTOPY_SITE_ORIGIN + path, description, inLanguage: "es-PY",
      isPartOf: { "@id": `${PUNTOPY_SITE_ORIGIN}/#website` }
    }
  };
}

export function robotsContent(seo: PageSeo): string {
  return seo.indexable ? "index, follow, max-image-preview:large" : "noindex, nofollow";
}

/** Usado en el build; nunca insertar JSON aportado por un comercio sin escapar '<'. */
export function serializeStructuredData(value: Record<string, unknown>): string {
  return JSON.stringify(value).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
}

export function applyPageSeo(seo: PageSeo): void {
  if (typeof document === "undefined") return;
  document.title = seo.title;
  const meta = (key: "name" | "property", name: string, content: string) => {
    let element = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${name}"]`);
    if (!element) { element = document.createElement("meta"); element.setAttribute(key, name); document.head.appendChild(element); }
    element.content = content;
  };
  meta("name", "description", seo.description);
  meta("name", "robots", robotsContent(seo));
  meta("property", "og:type", "website");
  meta("property", "og:site_name", PUNTOPY_SITE_NAME);
  meta("property", "og:locale", "es_PY");
  meta("property", "og:title", seo.title);
  meta("property", "og:description", seo.description);
  meta("property", "og:image", PUNTOPY_SITE_ORIGIN + PUNTOPY_SITE_IMAGE);
  meta("property", "og:image:alt", "Logotipo de PuntoPY");
  meta("name", "twitter:card", "summary");
  meta("name", "twitter:title", seo.title);
  meta("name", "twitter:description", seo.description);
  meta("name", "twitter:image", PUNTOPY_SITE_ORIGIN + PUNTOPY_SITE_IMAGE);
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (seo.path) {
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = PUNTOPY_SITE_ORIGIN + seo.path;
    meta("property", "og:url", canonical.href);
  } else {
    canonical?.remove();
    document.head.querySelector('meta[property="og:url"]')?.remove();
  }
  let structured = document.getElementById("puntopy-structured-data");
  if (seo.structuredData) {
    if (!structured) { const script = document.createElement("script"); script.type = "application/ld+json"; script.id = "puntopy-structured-data"; document.head.appendChild(script); structured = script; }
    structured.textContent = serializeStructuredData(seo.structuredData);
  } else structured?.remove();
}
